import { useCallback } from "react";
import { useToast } from "@hooks/use-toast";
import { MAX_FILE_NAME_LENGTH } from "@hooks/upload-tasks/upload-tasks-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { DataManagementEvents } from "@utils/track-event/track-event-list";
import { CheckFilesErrorToastType } from "@pages/data-management/data-management-types";
import { DEFAULT_BOLD_TEXT_FONT_WEIGHT } from "@styles/common-styles";
import { PHOTOGRAMMETRY_MAX_FILE_SIZE_IN_MB, SCAN_MAX_FILE_SIZE_IN_MB } from "@pages/data-management/import-data/import-data-utils";
import { FILE_SIZE_MULTIPLIER } from "@utils/file-utils";

type ReturnFunction = (
  type: CheckFilesErrorToastType,
  folderName?: string,
  projectName?: string,
  invalidFileNames?: string | string[]
) => void;

/** Provides various error toasts for Blink scan upload errors. */
export function useCheckFilesErrorToast(): ReturnFunction {
  const { showToast } = useToast();
  const { trackEvent } = useTrackEvent();

  return useCallback((
      errorType: CheckFilesErrorToastType, folderName?: string, projectName?: string, invalidFileNames?: string | string[]
    ): void => {
      let toastType: "info" | "success" | "warning" | "error" = "error";
      let message: React.ReactNode;
      let trackMessage: string | undefined;

      if (!invalidFileNames) {
        invalidFileNames = [];
      } else if (typeof invalidFileNames === "string") {
        invalidFileNames = [invalidFileNames];
      }

      switch (errorType) {
        case CheckFilesErrorToastType.tooLongFilename:
          message = "Folder or file name is too long. " +
            `Please change it to be shorter than ${MAX_FILE_NAME_LENGTH + 1} characters.`;
          break;
        case CheckFilesErrorToastType.tooLargeScans:
          message = "Some of the selected scans are too large. " +
            `The maximum file size is ${SCAN_MAX_FILE_SIZE_IN_MB / FILE_SIZE_MULTIPLIER} GB.`;
            break;
        case CheckFilesErrorToastType.tooLargePhotos:
          message = "Some of the selected photos are too large. " +
            `The maximum file size is ${PHOTOGRAMMETRY_MAX_FILE_SIZE_IN_MB} MB.`;
          break;
        case CheckFilesErrorToastType.collectFiles:
          message = "Unknown error when preparing the upload.";
          break;
        case CheckFilesErrorToastType.noFiles:
        case CheckFilesErrorToastType.noMultiFolder:
          message = "Please select a folder with valid project files. It should contain the file \"index-v2\".";
          break;
        case CheckFilesErrorToastType.duplicateFiles:
          message = "The selected folder contains duplicate scan files.";
          break;
        case CheckFilesErrorToastType.invalidFormat:
          message = "Some of the selected scans have an invalid format.";
          break;
        case CheckFilesErrorToastType.invalidMetadata:
          message = "Scan metadata is missing or invalid in the selected folder.";
          break;
        case CheckFilesErrorToastType.noNewFiles:
          toastType = "info";
          message = "All selected scans have already been uploaded.";
          break;
        case CheckFilesErrorToastType.noPhotogrammetryAccess:
          message = "You don't have access to the photogrammetry feature.";
          break;
        case CheckFilesErrorToastType.noFocusAccess:
          message = "Focus scan upload is not supported yet.";
          break;
        case CheckFilesErrorToastType.noOrbisAccess:
          message = "Orbis scan upload is not supported yet.";
          break;
        case CheckFilesErrorToastType.zeroBytesFiles:
          trackMessage = "Some files are invalid because they are empty.";
          message = (
            <>
              {trackMessage}<br/>
              Please retry the transfer from the device, or remove these files and try again.
            </>
          );
          break;
        default:
          errorType satisfies never;
          message = "Failed to read selected files. Please try again.";
          break;
      }

      trackEvent({
        name: DataManagementEvents.checkFilesError,
        props: {
          error: errorType,
          // if we have to output/render html use trackMessage with the string as the value here
          message: trackMessage ?? (typeof message === "string" ? message : ""),
        },
      });

      showToast({
        message,
        description: (folderName || projectName || invalidFileNames.length > 0) ?
          <div style={{ marginTop: "10px", fontWeight: DEFAULT_BOLD_TEXT_FONT_WEIGHT }}>
            {!!folderName && (
              <>
                Folder: <var>{folderName}</var>
              </>
            )}
            {!!projectName && (
              <>
                {(!!folderName) && <br />}
                Project: <var>{projectName}</var>
              </>
            )}
            {invalidFileNames.length > 0 && (
              <>
                {(!!folderName || !!projectName) && <br />}
                {invalidFileNames.length > 1
                  ? <>
                      Files:
                      <ul>
                        {invalidFileNames.map((name, index) =>
                          <li key={index}><var>{name}</var></li>
                        )}
                      </ul>
                    </>
                  : <>
                      File: <var>{invalidFileNames[0]}</var>
                    </>
                }
              </>
            )}
          </div> :
          undefined,
        type: toastType,
      });
    },
    [showToast, trackEvent]
  );
}
